import React, { useMemo, useState } from 'react'
import { Button, App, Spin } from 'antd'
import { useIntl } from 'react-intl'
import { EmailInviteStatusEnum } from '@vacationtracker/shared/types/user'

import IntlMessages from '../../../../util/IntlMessages'
import { track } from '../../../../services/analytics/analytics'
import { FormModal } from '../../../../design-system/FormModal'
import { UsersFilterBar, IUsersFilterBarProps } from './UsersFilterBar'
import { DIMENSION_TO_FIELD, hasActiveDimensionFilters } from '../hooks/useUsersFilters'
import { useExportDownload } from '../../../../hooks/use-export-download'
import { IUsersFilter } from '../types'

type IFilterBarOptions = Pick<IUsersFilterBarProps, 'statusOptions' | 'locations' | 'departments' | 'labels' | 'labelsEnabled' | 'isScopeLocked'>

export interface IExportUsersModalProps {
  open: boolean
  onClose: () => void
  filters: IUsersFilter
  inviteStatuses: EmailInviteStatusEnum[]
  inviteStatusOptions?: { id: string; name: string }[]
  filterBarOptions: IFilterBarOptions
  displayUnit: 'days' | 'hours'
  filteredTotal: number
  columns: string[]
  columnsReady: boolean
  authUser: { id: string; platform: string }
}

export const ExportUsersModal = (props: IExportUsersModalProps): React.ReactElement => {
  const { open, onClose, filters, inviteStatuses, inviteStatusOptions, filterBarOptions, displayUnit, filteredTotal, columns, columnsReady, authUser } = props
  const { formatMessage } = useIntl()
  const { notification } = App.useApp()
  const [localFilters, setLocalFilters] = useState<IUsersFilter>({ ...filters })
  const [localInviteStatuses, setLocalInviteStatuses] = useState<EmailInviteStatusEnum[]>(inviteStatuses)

  const { exportInProgress, exportLink, requestExport } = useExportDownload({
    storageKey: 'waiting_for_users_export',
    onError: (description) =>
      notification.error({
        message: formatMessage({ id: 'error.generic' }),
        ...(description ? { description } : {}),
        duration: 0,
      }),
  })

  const filtersUntouched = useMemo(
    () => JSON.stringify({ f: localFilters, i: localInviteStatuses }) === JSON.stringify({ f: filters, i: inviteStatuses }),
    [localFilters, localInviteStatuses, filters, inviteStatuses]
  )
  const hasActiveFilters = hasActiveDimensionFilters(localFilters) || localInviteStatuses.length > 0

  const buildQuery = (format: 'excel' | 'csv') => {
    const query: Record<string, unknown> = {
      reportType: 'user-summaries-report',
      exportFormat: format,
      display: displayUnit,
      userStatuses: [localFilters.status],
      columns,
    }
    if (localFilters.name?.trim()) {
      query.name = localFilters.name.trim()
    }
    if (localFilters.locationIds?.length) {
      query.locationIds = localFilters.locationIds
    }
    if (localFilters.teamIds?.length) {
      query.departmentIds = localFilters.teamIds
    }
    if (localFilters.labelIds?.length) {
      query.labelIds = localFilters.labelIds
    }
    if (localInviteStatuses.length) {
      query.inviteStatuses = localInviteStatuses
    }
    return query
  }

  const startExport = (format: 'excel' | 'csv') => {
    void requestExport(buildQuery(format), format)
    track('REPORT_DOWNLOADED', {
      platform: authUser.platform,
      userId: authUser.id,
      reportType: 'user-summaries-report',
    })
  }

  return (
    <FormModal open={open} title={formatMessage({ id: 'export.export' })} width="large" onClose={onClose} footer={null}>
      {!exportInProgress && !exportLink && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <UsersFilterBar
            filters={localFilters}
            {...filterBarOptions}
            collapseOnMobile={false}
            hasActiveFilters={hasActiveFilters}
            onStatusChange={(status) => setLocalFilters((previous) => ({ ...previous, status }))}
            onDimensionChange={(dimension, values) => {
              setLocalFilters((previous) => ({ ...previous, [DIMENSION_TO_FIELD[dimension]]: values }))
            }}
            onClearAll={() => {
              setLocalFilters((previous) => ({ ...previous, name: '', locationIds: [], teamIds: [], labelIds: [] }))
              setLocalInviteStatuses([])
            }}
            {...(inviteStatusOptions
              ? {
                  inviteStatuses: localInviteStatuses,
                  inviteStatusOptions,
                  onInviteStatusesChange: setLocalInviteStatuses,
                }
              : {})}
          />
          <div>{filtersUntouched ? <IntlMessages id="export.downloadAllRows" values={{ total: filteredTotal }} /> : <IntlMessages id="users.table.exportMatchingRows" />}</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button type="primary" disabled={!columnsReady} onClick={() => startExport('excel')}>
              {formatMessage({ id: 'export.fileTypeExcelXlsx' })}
            </Button>
            <Button disabled={!columnsReady} onClick={() => startExport('csv')}>
              {formatMessage({ id: 'export.fileTypeCsv' })}
            </Button>
          </div>
        </div>
      )}
      {exportInProgress && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <IntlMessages id="app.downloadProgressReport" />
          <Spin size="large" />
        </div>
      )}
      {exportLink && (
        <a href={exportLink} target="_blank" rel="noopener noreferrer">
          <Button type="primary" onClick={onClose}>
            <IntlMessages id="app.downloadReady" />
          </Button>
        </a>
      )}
    </FormModal>
  )
}
